import type {
  Candle,
  Horizon,
  Preset,
  PresetResolution,
  Round,
  Timeframe,
} from "../types";
import { INSTRUMENTS, type MarketGroup } from "./instruments";
import {
  entryForRound,
  indexMarkets,
  parseRoundIndex,
  type RoundIndex,
  type RoundIndexEntry,
} from "./roundIndex";
import type { TypedStorage } from "./storage";
import type { Setup } from "./setup";

/**
 * Runtime Round loading + selection (spec §11 `lib/rounds.ts`). Loads and
 * validates the shipped bank (dropping corrupt Rounds), and picks the next
 * unseen Round for the active mode filtered by settings — never repeating until
 * the eligible pool is exhausted, then reshuffling. Settings are passed in, so
 * this stays independent of the settings store.
 */

/**
 * The play modes (ADR-0006, extended by GRA-139): free play (Predict, Trade)
 * plus the Daily challenge, a finite seeded run rather than an endless Loop.
 */
export type Mode = "predict" | "trade" | "challenge";

// Timeframe and Horizon are declared in `types.ts` (the pipeline names them
// too, and this module is browser-only) and re-exported here, which is where
// the rest of the app has always imported them from.
export type { Horizon, Timeframe } from "../types";

/** Timeframes in the order the settings row lists them: shortest first. */
export const TIMEFRAMES: Timeframe[] = ["daily", "weekly", "monthly"];

/** Horizons in the order the settings row lists them. */
export const HORIZONS: Horizon[] = ["trend", "nextCandle"];

/** The player-facing name of a horizon, used by the settings row and shares. */
export const HORIZON_LABEL: Record<Horizon, string> = {
  nextCandle: "next candle",
  trend: "trend",
};

/** What each horizon asks the player to call, for the settings row's hints. */
export const HORIZON_HINT: Record<Horizon, string> = {
  nextCandle: "call the very next candle green or red",
  trend: "call which barrier price reaches first, upper or lower",
};

// ---------------------------------------------------------------------------
// Validation — drop corrupt Rounds on load (spec §13).
// ---------------------------------------------------------------------------

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null;
}

function isCandle(x: unknown): x is Candle {
  if (!isRecord(x)) return false;
  return (
    typeof x.time === "string" &&
    typeof x.open === "number" &&
    Number.isFinite(x.open) &&
    typeof x.high === "number" &&
    Number.isFinite(x.high) &&
    typeof x.low === "number" &&
    Number.isFinite(x.low) &&
    typeof x.close === "number" &&
    Number.isFinite(x.close) &&
    // volume is optional; when present it must be a finite number (GRA-133).
    (x.volume === undefined || (typeof x.volume === "number" && Number.isFinite(x.volume)))
  );
}

const PRESETS: Preset[] = ["easy", "normal", "hard"];

function isFiniteNumber(x: unknown): x is number {
  return typeof x === "number" && Number.isFinite(x);
}

function isPresetResolution(x: unknown): x is PresetResolution {
  if (!isRecord(x)) return false;
  return (
    (x.label === "up" || x.label === "down" || x.label === "sideways") &&
    (x.barrier === "up" || x.barrier === "down" || x.barrier === "time") &&
    isFiniteNumber(x.daysToResolve) &&
    isFiniteNumber(x.upperBarrier) &&
    isFiniteNumber(x.lowerBarrier) &&
    isFiniteNumber(x.pctMove)
  );
}

function hasReveal(x: Record<string, unknown>): boolean {
  return (
    typeof x.id === "string" &&
    typeof x.instrumentId === "string" &&
    typeof x.instrumentName === "string" &&
    isRecord(x.dateRange) &&
    typeof x.dateRange.start === "string" &&
    typeof x.dateRange.end === "string"
  );
}

export function isRound(x: unknown): x is Round {
  if (!isRecord(x) || !hasReveal(x)) return false;
  if (!Array.isArray(x.context) || x.context.length === 0) return false;
  if (!x.context.every(isCandle)) return false;
  if (!Array.isArray(x.forward) || !x.forward.every(isCandle)) return false;
  const resolutions = x.resolutions;
  if (!isRecord(resolutions)) return false;
  return PRESETS.every((preset) => isPresetResolution(resolutions[preset]));
}

/** Validate a raw bank array of any timeframe, dropping anything malformed. */
export function loadDailyRounds(raw: unknown): Round[] {
  return Array.isArray(raw) ? raw.filter(isRound) : [];
}

// ---------------------------------------------------------------------------
// Fetching a (possibly sharded) bank.
// ---------------------------------------------------------------------------

type BankManifest = { shards: string[]; count: number };
type FetchLike = (url: string) => Promise<{ json: () => Promise<unknown> }>;

function isManifest(x: unknown): x is BankManifest {
  return isRecord(x) && Array.isArray(x.shards);
}

/**
 * Fetch and validate a bank at `baseUrl` under `basename`, whatever its Round
 * shape. `<basename>.json` is either the full array (small bank) or a
 * `{ shards, count }` manifest, in which case each shard is fetched and
 * concatenated (spec §6 "sharded if large"). `parse` validates one array of
 * raw entries; anything malformed is dropped.
 */
async function fetchBank<R>(
  baseUrl: string,
  basename: string,
  parse: (raw: unknown) => R[],
  fetchFn: FetchLike,
): Promise<R[]> {
  const top = await (await fetchFn(`${baseUrl}/${basename}.json`)).json();
  if (!isManifest(top)) return parse(top);

  const rounds: R[] = [];
  for (const shard of top.shards) {
    const data = await (await fetchFn(`${baseUrl}/${shard}`)).json();
    rounds.push(...parse(data));
  }
  return rounds;
}

/** Fetch and validate a barrier bank at `baseUrl` under `basename`. */
export function fetchBarrierBank(
  baseUrl: string,
  basename: string,
  fetchFn: FetchLike = fetch,
): Promise<Round[]> {
  return fetchBank(baseUrl, basename, loadDailyRounds, fetchFn);
}

/** Fetch and validate the daily bank: `rounds.json`, sharded when large. */
export function fetchDailyBank(
  baseUrl: string,
  fetchFn: FetchLike = fetch,
): Promise<Round[]> {
  return fetchBarrierBank(baseUrl, "rounds", fetchFn);
}

const bankCache = new Map<string, Promise<unknown[]>>();

/**
 * A bank, loaded at most once per `baseUrl` per session.
 *
 * Several Loops share one bank (Predict-trend, Predict-next-candle and, on
 * daily, Trade all read the same tens of MB across several shards). Fetched per
 * Loop, the second one selected re-downloaded and re-validated all of it, so
 * switching sat on "loading rounds…" with no chart. Sharing the promise makes
 * the switch reuse the already-parsed Rounds. A failed load is evicted, so
 * re-selecting the Loop still retries (the behavior App's `initialized`
 * bookkeeping expects).
 *
 * The weekly and monthly banks go through the same cache even though only one
 * Loop reads each: it is what lets the idle prefetch (lib/prefetch.ts) warm a
 * bank without the Loop that later selects it fetching a second copy.
 *
 * `fetchDailyBank` / `fetchWeeklyBank` / `fetchMonthlyBank` stay the uncached
 * primitives: those are what tests drive with an injected `fetchFn`.
 */
function sharedBank<R>(
  baseUrl: string,
  basename: string,
  load: () => Promise<R[]>,
): Promise<R[]> {
  const key = `${baseUrl}/${basename}`;
  const cached = bankCache.get(key);
  if (cached) return cached as Promise<R[]>;

  const pending = load().catch((error: unknown) => {
    bankCache.delete(key);
    throw error;
  });
  bankCache.set(key, pending);
  return pending;
}

/** The daily bank, shared by Predict-daily (both horizons) and Trade. */
export function sharedDailyBank(
  baseUrl: string,
  fetchFn: FetchLike = fetch,
): Promise<Round[]> {
  return sharedBank(baseUrl, "rounds", () =>
    fetchBarrierBank(baseUrl, "rounds", fetchFn),
  );
}

/** The weekly bank, shared between the Loop that plays it and the prefetch. */
export function sharedWeeklyBank(
  baseUrl: string,
  fetchFn: FetchLike = fetch,
): Promise<Round[]> {
  return sharedBank(baseUrl, "weekly", () =>
    fetchBarrierBank(baseUrl, "weekly", fetchFn),
  );
}

/** The monthly bank, shared between the Loop that plays it and the prefetch. */
export function sharedMonthlyBank(
  baseUrl: string,
  fetchFn: FetchLike = fetch,
): Promise<Round[]> {
  return sharedBank(baseUrl, "monthly", () =>
    fetchBarrierBank(baseUrl, "monthly", fetchFn),
  );
}

/** Forget the shared banks and sources (test isolation). */
export function resetSharedDailyBank(): void {
  bankCache.clear();
  sourceCache.clear();
}

/** Fetch and validate the weekly bank, sharded when large. */
export function fetchWeeklyBank(
  baseUrl: string,
  fetchFn: FetchLike = fetch,
): Promise<Round[]> {
  return fetchBarrierBank(baseUrl, "weekly", fetchFn);
}

/** Fetch and validate the monthly bank, sharded when large. */
export function fetchMonthlyBank(
  baseUrl: string,
  fetchFn: FetchLike = fetch,
): Promise<Round[]> {
  return fetchBarrierBank(baseUrl, "monthly", fetchFn);
}

// ---------------------------------------------------------------------------
// The Round source: an index up front, shards on demand (GRA-152, ADR-0012).
// ---------------------------------------------------------------------------

/**
 * A bank, as the Loops now see it: everything selection needs, loaded up
 * front; the candles of one Round, fetched when that Round is actually about
 * to be played.
 *
 * `peek` is what keeps the lifecycle honest about the difference. A Round
 * whose shard is already parsed is available *now*, with no promise and no
 * loading state, which is the common case once the previous Round warmed its
 * neighbour; only a genuine miss costs a fetch. Every Loop reading a bank
 * shares one source, so a shard fetched for Predict is already there for Trade.
 */
export type RoundSource<R> = {
  /** Every Round in the bank, without its candles. */
  index: RoundIndexEntry[];
  /** The instruments the bank carries, for the markets list. */
  instruments: { id: string; name: string }[];
  /** The Round if its shard is already parsed, else null. Never fetches. */
  peek: (id: string) => R | null;
  /** The Round, fetching and parsing its shard if needed. */
  get: (id: string) => Promise<R | null>;
  /** Warm every shard `ids` needs, concurrently. Resolves with the Rounds. */
  getMany: (ids: string[]) => Promise<R[]>;
};

/** Build a source over `index`, fetching shards from `baseUrl` on demand. */
export function createRoundSource<R extends { id: string }>(
  baseUrl: string,
  index: RoundIndex,
  parse: (raw: unknown) => R[],
  fetchFn: FetchLike = fetch,
): RoundSource<R> {
  const shardOf = new Map(index.entries.map((entry) => [entry.id, entry.shard]));
  // One promise per shard, so two Rounds from the same shard (and two Loops
  // asking at once) share a single request and a single parse.
  const shards = new Map<string, Promise<void>>();
  const rounds = new Map<string, R>();

  const loadShard = (shard: string): Promise<void> => {
    const pending = shards.get(shard);
    if (pending) return pending;
    const started = (async () => {
      const raw = await (await fetchFn(`${baseUrl}/${shard}`)).json();
      for (const round of parse(raw)) rounds.set(round.id, round);
    })().catch((error: unknown) => {
      // Evict, so a failed shard is retried rather than remembered as empty.
      shards.delete(shard);
      throw error;
    });
    shards.set(shard, started);
    return started;
  };

  const get = async (id: string): Promise<R | null> => {
    const cached = rounds.get(id);
    if (cached) return cached;
    const shard = shardOf.get(id);
    if (!shard) return null;
    await loadShard(shard);
    return rounds.get(id) ?? null;
  };

  return {
    index: index.entries,
    instruments: indexMarkets(index),
    peek: (id) => rounds.get(id) ?? null,
    get,
    getMany: (ids) =>
      Promise.all(ids.map((id) => get(id))).then((settled) =>
        (settled as (R | null)[]).filter((round): round is R => round !== null),
      ),
  };
}

/**
 * A source over an already-loaded array of Rounds: no index file, no shards,
 * every Round available synchronously. This is what `hydrate` uses, so a test
 * (and the "same ids either way" proof in roundIndex.test.ts) drives the real
 * lifecycle against a plain array.
 */
export function sourceFromRounds<R extends { id: string; instrumentId: string }>(
  rounds: R[],
): RoundSource<R> {
  const byId = new Map(rounds.map((round) => [round.id, round]));
  const entries = rounds.map((round) =>
    entryForRound(asIndexable(round), "memory", groupOf(round.instrumentId)),
  );
  const instruments = [
    ...new Map(entries.map((entry) => [entry.instrumentId, entry.instrumentName])),
  ].map(([id, name]) => ({ id, name }));
  return {
    index: entries,
    instruments,
    peek: (id) => byId.get(id) ?? null,
    get: (id) => Promise.resolve(byId.get(id) ?? null),
    getMany: (ids) =>
      Promise.resolve(
        ids.map((id) => byId.get(id)).filter((round): round is R => round !== undefined),
      ),
  };
}

function groupOf(instrumentId: string): MarketGroup {
  return INSTRUMENTS.find((i) => i.id === instrumentId)?.group ?? "indices";
}

/**
 * Fill in what a real Round carries and a test double may not (a name, a date
 * range, a Context). A memory source is a convenience for tests and for the
 * synchronous `hydrate` path, so it must not demand more of a Round than
 * the lifecycle itself does.
 */
function asIndexable(round: {
  id: string;
  instrumentId: string;
  instrumentName?: string;
  context?: unknown[];
  dateRange?: { start: string; end: string };
  setup?: Setup;
  resolutions?: Record<Preset, { barrier: "up" | "down" | "time" }>;
}): Parameters<typeof entryForRound>[0] {
  return {
    id: round.id,
    instrumentId: round.instrumentId,
    instrumentName: round.instrumentName ?? round.instrumentId,
    context: round.context ?? [],
    dateRange: round.dateRange ?? { start: "", end: "" },
    setup: round.setup,
    resolutions: round.resolutions,
  };
}

/**
 * Fetch `<basename>-index.json` and build a source over it. A missing or
 * unparseable index throws, which is what surfaces the "Couldn't load rounds"
 * screen: without an index there is nothing to select from, and guessing at
 * shard names would only turn a clear failure into a slow one.
 */
export async function fetchRoundSource<R extends { id: string }>(
  baseUrl: string,
  basename: string,
  parse: (raw: unknown) => R[],
  fetchFn: FetchLike = fetch,
): Promise<RoundSource<R>> {
  const raw = await (await fetchFn(`${baseUrl}/${basename}-index.json`)).json();
  const index = parseRoundIndex(raw);
  if (!index || index.entries.length === 0) {
    throw new Error(`No usable Round index at ${baseUrl}/${basename}-index.json`);
  }
  return createRoundSource(baseUrl, index, parse, fetchFn);
}

const sourceCache = new Map<string, Promise<RoundSource<never>>>();

/**
 * A bank's source, opened at most once per `baseUrl` per session.
 *
 * The reason is the same one the old whole-bank cache had (several Loops read
 * the daily bank, and each must not re-download it), but the stakes are lower
 * and the win is larger: sharing the source also shares its parsed shards, so
 * a Round already fetched for Predict is free for Trade and for the challenge.
 * A failed open is evicted, so re-selecting the Loop retries.
 */
function sharedSource<R extends { id: string }>(
  baseUrl: string,
  basename: string,
  open: () => Promise<RoundSource<R>>,
): Promise<RoundSource<R>> {
  const key = `${baseUrl}/${basename}`;
  const cached = sourceCache.get(key);
  if (cached) return cached as unknown as Promise<RoundSource<R>>;

  const pending = open().catch((error: unknown) => {
    sourceCache.delete(key);
    throw error;
  });
  sourceCache.set(key, pending as unknown as Promise<RoundSource<never>>);
  return pending;
}

/** The daily source, shared by Predict-daily (both horizons), Trade and the challenge. */
export function sharedDailySource(
  baseUrl: string,
  fetchFn: FetchLike = fetch,
): Promise<RoundSource<Round>> {
  return sharedSource(baseUrl, "rounds", () =>
    fetchRoundSource(baseUrl, "rounds", loadDailyRounds, fetchFn),
  );
}

/** The weekly source, shared by Predict-weekly (both horizons) and Trade. */
export function sharedWeeklySource(
  baseUrl: string,
  fetchFn: FetchLike = fetch,
): Promise<RoundSource<Round>> {
  return sharedSource(baseUrl, "weekly", () =>
    fetchRoundSource(baseUrl, "weekly", loadDailyRounds, fetchFn),
  );
}

/** The monthly source, shared by Predict-monthly (both horizons) and Trade. */
export function sharedMonthlySource(
  baseUrl: string,
  fetchFn: FetchLike = fetch,
): Promise<RoundSource<Round>> {
  return sharedSource(baseUrl, "monthly", () =>
    fetchRoundSource(baseUrl, "monthly", loadDailyRounds, fetchFn),
  );
}

/**
 * A timeframe's Round source. Every bank has the same shape (ADR-0013), so a
 * Loop that plays whatever the timeframe setting says (Trade) picks its source
 * through here rather than importing one bank's loader.
 */
export const TIMEFRAME_SOURCE: Record<
  Timeframe,
  (baseUrl: string, fetchFn?: FetchLike) => Promise<RoundSource<Round>>
> = {
  daily: sharedDailySource,
  weekly: sharedWeeklySource,
  monthly: sharedMonthlySource,
};

/** Forget the shared sources (test isolation). */
export function resetSharedSources(): void {
  sourceCache.clear();
}

// ---------------------------------------------------------------------------
// Dev-only pinned Round override.
// ---------------------------------------------------------------------------

/**
 * The `?round=<id>` override for reproducible visual inspection during
 * development (e.g. browser automation reloading the same chart). Reads
 * `import.meta.env.DEV`, so it is inert in a production build regardless of
 * the URL. Returns null with no `window` (the jsdom/test path is otherwise
 * fine) or with the param absent or empty.
 */
export function pinnedRoundId(): string | null {
  if (!import.meta.env.DEV) return null;
  if (typeof window === "undefined") return null;
  const id = new URLSearchParams(window.location.search).get("round");
  return id && id.length > 0 ? id : null;
}

// ---------------------------------------------------------------------------
// Selection.
// ---------------------------------------------------------------------------

/**
 * The settings that constrain which Rounds are eligible. Difficulty itself is
 * deliberately absent from instrument/context filtering: per ADR-0004 every
 * stored Round is eligible (has a resolution) at all presets, so difficulty
 * ordinarily just selects which precomputed resolution to read, never narrows
 * the pool. `barrierPreset` is the one preset-driven filter (GRA-140), and it
 * is narrower than "eligible": it asks whether that resolution actually hits a
 * barrier, for the trend Loops only. Do not add any other difficulty filter
 * here without revisiting that ADR.
 */
export type RoundFilter = {
  /** Instrument ids to draw from; empty/undefined means all markets. */
  instruments?: string[];
  /** Require a Round to carry at least this many Context candles. */
  minContextLength?: number;
  /**
   * Trend Loops only (GRA-140): require the Round's resolution at this preset
   * to resolve by a barrier hit ("up" or "down"), not the time cap. Rounds
   * without a per-preset resolution (the Next-Candle banks) are unaffected,
   * since they have nothing to check.
   */
  barrierPreset?: Preset;
};

/**
 * A pickable Round: anything with an id, an instrument, a Context length and
 * (for a barrier bank) its per-preset barrier outcomes.
 *
 * Both a full Round and a `RoundIndexEntry` satisfy this, which is the point:
 * selection runs identically whether it is handed the index or a fully loaded
 * bank. A Round says how long its Context is by carrying one; an index entry
 * says so with a number, because carrying 50 candles to answer "at least 40?"
 * is the whole cost this seam exists to avoid.
 */
type Selectable = {
  id: string;
  instrumentId: string;
  context?: unknown[];
  contextLength?: number;
  resolutions?: Record<Preset, PresetResolution | { barrier: string }>;
};

/** How many Context candles a selectable carries, however it says so. */
function contextLengthOf(round: Selectable): number {
  return round.contextLength ?? round.context?.length ?? 0;
}

export type Selection<R> = {
  round: R;
  /** The eligible pool was exhausted; seen Rounds were reused this pick. */
  reshuffled: boolean;
  /** The filter matched nothing, so it was relaxed to the nearest non-empty. */
  fellBack: boolean;
  warning?: string;
};

/** Whether `round` satisfies `filter`. Exported for the Loop lifecycle
 * (roundLoop.ts), which re-checks an on-screen Round against a changed
 * filter (GRA-140) without redoing the whole selection. */
export function matchesFilter(round: Selectable, filter: RoundFilter): boolean {
  if (
    filter.instruments &&
    filter.instruments.length > 0 &&
    !filter.instruments.includes(round.instrumentId)
  ) {
    return false;
  }
  if (
    filter.minContextLength !== undefined &&
    contextLengthOf(round) < filter.minContextLength
  ) {
    return false;
  }
  if (filter.barrierPreset !== undefined) {
    const resolution = round.resolutions?.[filter.barrierPreset];
    // A Round with no per-preset resolution at all (the Next-Candle banks)
    // has nothing to check, so it passes through unaffected: only a Round
    // that actually carries a "time" resolution at this preset is rejected.
    if (resolution && resolution.barrier === "time") return false;
  }
  return true;
}

function pickRandom<R>(items: R[], rng: () => number): R {
  const index = Math.min(items.length - 1, Math.floor(rng() * items.length));
  return items[index];
}

/**
 * Pick the next Round to play. Filters `pool` by `filter`, prefers a Round whose
 * id is not in `seenIds`, and reshuffles (reusing seen Rounds) once every
 * eligible Round has been seen. If the filter yields an empty pool it is relaxed
 * to the nearest non-empty set (dropping the market filter, then all filters)
 * with a warning. Returns `null` only for a genuinely empty `pool`.
 */
export function selectNextRound<R extends Selectable>(
  pool: R[],
  seenIds: Iterable<string>,
  filter: RoundFilter,
  rng: () => number = Math.random,
): Selection<R> | null {
  if (pool.length === 0) return null;

  let eligible = pool.filter((r) => matchesFilter(r, filter));
  let fellBack = false;
  let warning: string | undefined;

  if (eligible.length === 0) {
    fellBack = true;
    // Relax the market filter first, keeping any context-length requirement
    // and the barrier requirement (GRA-140): a time-cap Round is never an
    // acceptable fallback for the trend Loops, only a wider market pool is.
    eligible = pool.filter((r) =>
      matchesFilter(r, {
        minContextLength: filter.minContextLength,
        barrierPreset: filter.barrierPreset,
      }),
    );
    warning =
      "No Rounds match the selected markets — showing all markets instead.";
    if (eligible.length === 0) {
      // Last resort: drop the barrier requirement too, rather than serving
      // nothing. In practice the daily bank's barrier-hit rate (GRA-140) never
      // gets close to empty even at Hard, so this path is not expected to run.
      eligible = pool.slice();
      warning = "No Rounds match the current settings — showing all Rounds.";
    }
  }

  const seen = new Set(seenIds);
  const unseen = eligible.filter((r) => !seen.has(r.id));

  const reshuffled = unseen.length === 0;
  const candidates = reshuffled ? eligible : unseen;

  return { round: pickRandom(candidates, rng), reshuffled, fellBack, warning };
}

// ---------------------------------------------------------------------------
// Seen-Round tracking, persisted per Round set.
// ---------------------------------------------------------------------------

export interface SeenTracker {
  has(id: string): boolean;
  add(id: string): void;
  reset(): void;
  ids(): string[];
}

/**
 * A seen-Round id set persisted under `key` (one key per Round set, e.g.
 * "seen:daily" / "seen:weekly") via the typed storage wrapper.
 */
export function createSeenTracker(
  store: TypedStorage,
  key: string,
): SeenTracker {
  const ids = new Set<string>(store.get<string[]>(key, []));
  const persist = () => store.set(key, [...ids]);
  return {
    has: (id) => ids.has(id),
    add: (id) => {
      ids.add(id);
      persist();
    },
    reset: () => {
      ids.clear();
      persist();
    },
    ids: () => [...ids],
  };
}
